import { lazy, Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  formatInstant,
  isApiError,
  links,
  resolveFormattingLocale,
  type ReportFormat,
} from '@eis/core/reporting';
import {
  ConfirmAction,
  ErrorState,
  PageHeader,
  PageLayout,
  RequirePermission,
  cardSurfaceSx,
  usePermissions,
} from '@eis/patterns';
import {
  FieldList,
  FieldShelf,
  FilterValueInput,
  FormatDialog,
  LiveRegion,
  QueryRunState,
  displayFieldName,
  type CalculatedField,
  type FieldMenuAction,
  type FilterTimeZone,
  type ShelfDrop,
  type ShelfItem,
} from '@eis/patterns/reporting';
import { reportingPermissions as p } from '../../../permissions';
import {
  addField,
  asDocument,
  isRunnable,
  moveToken,
  newReport,
  removeToken,
  reorder,
  setAggregation,
  setBucket,
  setPresentation,
  setReportType,
  shelfEntries,
  shelfLimit,
  shelvesOf,
  valueKey,
  type Aggregation,
  type Bucket,
  type ChartDoc,
  type FieldType,
  type MoveResult,
  type ReportDocument,
  type ReportType,
  type Shelf,
} from '../../../lib/document';
import { codeMessage } from '../../../lib/codes';
import { hasResultData, workspaceFormatsOf } from '../../../lib/result';
import { DEFAULT_WORKSPACE_FORMATS } from '@eis/core/reporting';
import {
  documentViolationsOf,
  isConflictError,
  reportingReasonOf,
  useCustomDataset,
  useDataset,
  useDatasetFieldValues,
  useDeleteReport,
  useDiscardReportDraft,
  useFetchDatasetFields,
  useFetchReportVersion,
  usePreviewReport,
  usePublishReportDraft,
  usePutReportDraft,
  useQueryPolling,
  useReport,
  useReportVersions,
  useUpdateReport,
  useValidateExpression,
  useWorkspaceSettings,
  type Report,
} from '../api/editor';
import { fieldListOf, operatorsFor, shelfItemsOf, shelfTitle, typesOf } from '../model/shelves';
import { ReportResult, type ResultMode } from '../../report-viewer/ui/ReportResult';
import { DatasetPicker, datasetKey, type DatasetOption } from '../../reports-list/ui/DatasetPicker';
import {
  LIST_OPS,
  RANGE_OP,
  canonicalExpression,
  clausesOfPicker,
  pickerOfClause,
  putClauses,
  renameField,
  setSort,
  sortOf,
  usesField,
  type MissingValue,
  type PickerState,
} from '../model/edits';

export interface ReportEditorPageProps {
  readonly code: string;
}

/**
 * The calculated-field dialog with its suggestions and function help, in a chunk of its own: it
 * opens on demand, and the `module-reporting` budget wants a lazy boundary, not a raise. Rendered
 * (closed) with the editor, so it is fetched while the analyst looks at the report.
 */
const ExpressionDialog = lazy(async () => {
  const m = await import('@eis/patterns/reporting-expression');
  return { default: m.ExpressionDialog };
});

const AUTOSAVE_MS = 800;
const PREVIEW_MS = 500;
const AGGREGATIONS: readonly Aggregation[] = [
  'SUM',
  'COUNT',
  'COUNT_DISTINCT',
  'AVG',
  'MIN',
  'MAX',
];
const BUCKETS: readonly Bucket[] = ['YEAR', 'QUARTER', 'MONTH', 'DAY'];
const CHART_TYPES: readonly ChartDoc['type'][] = [
  'BAR',
  'STACKED_BAR',
  'HORIZONTAL_BAR',
  'LINE',
  'AREA',
  'PIE',
  'KPI',
];

/**
 * The report editor (SDD-024 U-1…U-3, R-2, R-5, R-6, D-5; mockup boards 4–6 and 10): the dataset's
 * fields, the four shelves, the live preview. Every change autosaves the draft (debounced, with the
 * head's ETag — a stale write is the conflict banner, never a silent overwrite) and re-runs the
 * preview 500 ms after the last change with the unsaved document. Publish freezes the draft once
 * it ran successfully; the visibility, the JSON of the document and "Neuere Version verfügbar" for
 * a custom dataset are here too. Every drag has a keyboard twin, and every move is announced.
 */
export function ReportEditorPage({ code }: ReportEditorPageProps): ReactNode {
  const { t } = useTranslation('reporting');
  const report = useReport(code);
  const [generation, setGeneration] = useState(0);
  const data = report.data;
  const start =
    data === undefined ? undefined : (asDocument(data.draft) ?? asDocument(data.document));
  if (report.isLoading) {
    return (
      <PageLayout header={<PageHeader title={t('editor.breadcrumb')} />}>
        <Skeleton variant="rounded" height={480} />
      </PageLayout>
    );
  }
  if (report.isError || data === undefined || start === undefined) {
    return (
      <PageLayout header={<PageHeader title={t('editor.breadcrumb')} />}>
        <ErrorState
          description={
            isApiError(report.error) && report.error.status === 404
              ? t('editor.notFound')
              : t('editor.loadError')
          }
          onRetry={() => void report.refetch()}
        />
      </PageLayout>
    );
  }
  // A reload (the conflict banner, a discarded draft) re-reads the head and starts the editor again.
  return (
    <EditorBody
      key={generation}
      code={code}
      initial={data}
      start={start}
      onReload={() => {
        void report.refetch().then(() => {
          setGeneration((g) => g + 1);
        });
      }}
    />
  );
}

interface EditorBodyProps {
  readonly code: string;
  readonly initial: Report;
  readonly start: ReportDocument;
  readonly onReload: () => void;
}

function EditorBody({ code, initial, start, onReload }: EditorBodyProps): ReactNode {
  const { t, i18n } = useTranslation('reporting');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const permissions = usePermissions();
  const report = { data: initial };
  const settings = useWorkspaceSettings();
  const save = usePutReportDraft();
  const preview = usePreviewReport();
  const publish = usePublishReportDraft();
  const discard = useDiscardReportDraft();
  const update = useUpdateReport();
  const remove = useDeleteReport();
  const validate = useValidateExpression();
  const versions = useReportVersions(code);
  const fetchVersion = useFetchReportVersion();
  const fieldsOf = useFetchDatasetFields();

  // The editor starts from the draft, else from the latest published version (R-2).
  const [doc, setDoc] = useState<ReportDocument>(start);
  const [head, setHead] = useState<{
    etag?: string;
    version?: string;
    publishedVersion: number;
    visibility: string;
  }>(() => ({
    ...(initial.etag === undefined ? {} : { etag: initial.etag }),
    ...(initial.version === undefined ? {} : { version: initial.version }),
    publishedVersion: initial.publishedVersion ?? 0,
    visibility: initial.visibility ?? 'PRIVATE',
  }));
  const [savedJson, setSavedJson] = useState(() => JSON.stringify(start));
  const [savedAt, setSavedAt] = useState<string | undefined>(undefined);
  const [conflict, setConflict] = useState(false);
  const [refusal, setRefusal] = useState<
    { code?: string; violations: { field: string; description: string }[] } | undefined
  >(undefined);
  const [publishedAs, setPublishedAs] = useState<number | undefined>(undefined);
  const [previewId, setPreviewId] = useState<string | undefined>(undefined);
  const [mode, setMode] = useState<ResultMode>('auto');
  const [announcement, setAnnouncement] = useState('');
  const [picker, setPicker] = useState<PickerState | undefined>(undefined);
  const [formatTarget, setFormatTarget] = useState<{ shelf: Shelf; key: string } | undefined>(
    undefined,
  );
  const [expressionOpen, setExpressionOpen] = useState(false);
  const [jsonOpen, setJsonOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | undefined>(undefined);
  const [deleting, setDeleting] = useState(false);
  // Whether the head has a draft: from the read, then from this session's saves (an autosave makes
  // one) — "Entwurf verwerfen" follows it (owner feedback 2026-11-01).
  const [hasDraft, setHasDraft] = useState(initial.hasDraft === true);
  const [discarding, setDiscarding] = useState(false);
  const [notice, setNotice] = useState<string | undefined>(undefined);
  const [missing, setMissing] = useState<MissingValue | undefined>(undefined);
  // How a date-time filter value is entered: the browser's zone or UTC (the warehouse stores UTC).
  const [zone, setZone] = useState<FilterTimeZone>('LOCAL');
  const [calcEditing, setCalcEditing] = useState<CalculatedField | undefined>(undefined);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [restoring, setRestoring] = useState<number | undefined>(undefined);
  const [restoreError, setRestoreError] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [switchTo, setSwitchTo] = useState<DatasetOption | undefined>(undefined);
  const polling = useQueryPolling(previewId);

  const datasetRef = doc?.dataset;
  const catalog = useDataset(datasetRef?.kind === 'CATALOG' ? datasetRef.name : undefined);
  const custom = useCustomDataset(datasetRef?.kind === 'CUSTOM' ? datasetRef.code : undefined);
  const datasetFields = useMemo(
    () =>
      datasetRef?.kind === 'CUSTOM' ? (custom.data?.fields ?? []) : (catalog.data?.fields ?? []),
    [datasetRef?.kind, custom.data, catalog.data],
  );
  const types = useMemo(
    () => (doc === undefined ? new Map<string, FieldType>() : typesOf(datasetFields, doc)),
    [datasetFields, doc],
  );
  const editable = report.data?.editable === true && permissions.can(p.reports.update);

  /** Saves the draft with the head's ETag; answers the new head, or undefined when refused. */
  const persist = useCallback(
    async (next: ReportDocument): Promise<{ etag?: string; version?: string } | undefined> => {
      try {
        const saved = await save.mutateAsync({
          code,
          document: next as unknown as Record<string, unknown>,
          etag: head.etag,
          version: head.version,
        });
        const moved = {
          ...(saved.etag === undefined ? {} : { etag: saved.etag }),
          ...(saved.version === undefined ? {} : { version: saved.version }),
        };
        setHead((prev) => ({ ...prev, ...moved }));
        setHasDraft(true);
        setSavedJson(JSON.stringify(next));
        setSavedAt(new Date().toISOString());
        setRefusal(undefined);
        return moved;
      } catch (error) {
        if (isConflictError(error)) setConflict(true);
        else {
          const reason = reportingReasonOf(error);
          setRefusal({
            ...(reason === undefined ? {} : { code: reason }),
            violations: documentViolationsOf(error),
          });
        }
        return undefined;
      }
    },
    [save, code, head.etag, head.version],
  );

  // Autosave (R-2): debounced, never while a conflict is unresolved.
  const json = doc === undefined ? '' : JSON.stringify(doc);
  useEffect(() => {
    if (doc === undefined || !editable || conflict || json === savedJson) return undefined;
    const timer = setTimeout(() => {
      void persist(doc);
    }, AUTOSAVE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [json, savedJson, editable, conflict, doc, persist]);

  // The preview (U-1): 500 ms after the last change, with the unsaved document.
  useEffect(() => {
    if (doc === undefined || !editable || !isRunnable(doc)) return undefined;
    const timer = setTimeout(() => {
      preview.mutate(
        { code, document: doc as unknown as Record<string, unknown> },
        {
          onSuccess: (query) => {
            setPreviewId(query.id);
          },
        },
      );
    }, PREVIEW_MS);
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [json, editable]);

  const apply = (result: MoveResult, item: string, shelf: Shelf): void => {
    if (doc === undefined) return;
    const shelfName = shelfTitle(doc, shelf, t);
    if (result.ok) {
      setDoc(result.doc);
      setAnnouncement(
        t('editor.moved', {
          item,
          shelf: shelfName,
          position: result.position,
          count: result.count,
        }),
      );
    } else if (result.reason === 'full') {
      setAnnouncement(t('editor.full', { shelf: shelfName, max: shelfLimit(doc, shelf) }));
    } else if (result.reason === 'duplicate') {
      setAnnouncement(t('editor.duplicate', { item, shelf: shelfName }));
    }
  };

  // Value suggestions of a STRING field (SQL-6) — catalog datasets only; a custom dataset's values
  // are typed.
  const valuesOf = useDatasetFieldValues(
    datasetRef?.kind === 'CATALOG' ? datasetRef.name : undefined,
  );
  const showPicker = (next: PickerState): void => {
    setPicker(next);
    setMissing(undefined);
  };
  const pickerField = picker?.field ?? '';
  const loadPickerValues = useCallback(
    (prefix: string) => valuesOf(pickerField, prefix),
    [valuesOf, pickerField],
  );

  // A new filter opens on the operator its type is most often filtered by: a text field on "ist
  // eines von" (the checkbox list), a number or a date on "zwischen".
  const openPicker = (field: string): void => {
    const type = types.get(field);
    const ops = operatorsFor(type);
    const op = type === 'STRING' || type === undefined ? '=()' : (ops[0] ?? '==');
    showPicker({ field, op, values: [], editable: false, label: '' });
  };

  const moveField = (field: string, shelf: Shelf): void => {
    if (doc === undefined) return;
    if (shelf === 'filters') {
      openPicker(field);
      return;
    }
    apply(addField(doc, field, shelf, types.get(field)), displayFieldName(field), shelf);
  };

  const onDrop = (shelf: Shelf, drop: ShelfDrop): void => {
    if (doc === undefined) return;
    if (drop.kind === 'field') {
      moveField(drop.field, shelf);
      return;
    }
    const entry = shelfEntries(doc, drop.from).find((e) => e.key === drop.key);
    if (entry === undefined) return;
    if (shelf === 'filters') {
      openPicker(entry.field);
      return;
    }
    apply(
      moveToken(doc, drop.from, drop.key, shelf, types.get(entry.field)),
      displayFieldName(entry.field),
      shelf,
    );
  };

  const draftVersion = head.publishedVersion + 1;
  const dirty = json !== savedJson;
  const publishRefusal = publish.error === null ? undefined : reportingReasonOf(publish.error);
  const newer = doc.dataset.kind === 'CUSTOM' ? (custom.data?.dataset?.publishedVersion ?? 0) : 0;
  const pinned = doc.dataset.kind === 'CUSTOM' ? doc.dataset.version : 0;
  const query = polling.query;
  const failure = query?.error?.code;
  const message = codeMessage(t, failure);
  const workspace = workspaceFormatsOf(settings.data?.document) ?? DEFAULT_WORKSPACE_FORMATS;
  const formatShelfType = (shelf: Shelf, key: string): FieldType => {
    if (shelf === 'values') {
      const v = doc.pivot?.values.find((x) => valueKey(x) === key);
      return v !== undefined && (v.aggregation === 'MIN' || v.aggregation === 'MAX')
        ? (types.get(v.field) ?? 'NUMBER')
        : 'NUMBER';
    }
    const entry = shelfEntries(doc, shelf).find((e) => e.key === key);
    return types.get(entry?.field ?? '') ?? 'STRING';
  };

  const displayActions =
    (shelf: Shelf) =>
    (item: ShelfItem): FieldMenuAction[] => {
      // Title and format live in one dialog (D-4: the editor writes one place).
      const presentation: FieldMenuAction[] = [
        {
          label: i18n.t('patterns:reporting.shelf.title'),
          onClick: () => {
            setFormatTarget({ shelf, key: item.key });
          },
        },
        {
          label: i18n.t('patterns:reporting.shelf.format'),
          onClick: () => {
            setFormatTarget({ shelf, key: item.key });
          },
        },
      ];
      if (shelf === 'filters') {
        const index = Number(item.key);
        const opened = pickerOfClause(doc.filters?.clauses ?? [], index);
        return [
          ...(opened === undefined || !editable
            ? []
            : [
                {
                  label: t('editor.editFilter'),
                  onClick: () => {
                    showPicker(opened);
                  },
                },
              ]),
          ...presentation,
        ];
      }
      // The report's default sort (owner feedback 2026-11-01): on a table column, a pivot's row or
      // column field, or a value — one key, set from the token's menu.
      const sortBy =
        shelf === 'values'
          ? item.key
          : (shelfEntries(doc, shelf).find((e) => e.key === item.key)?.field ?? item.key);
      const current = sortOf(doc).find((s) => s.by === sortBy)?.direction;
      const sorting: FieldMenuAction[] = editable
        ? [
            {
              label: `${t('editor.sortAscending')}${current === 'ASC' ? ' ✓' : ''}`,
              disabled: current === 'ASC',
              onClick: () => {
                setDoc(setSort(doc, sortBy, 'ASC'));
              },
            },
            {
              label: `${t('editor.sortDescending')}${current === 'DESC' ? ' ✓' : ''}`,
              disabled: current === 'DESC',
              onClick: () => {
                setDoc(setSort(doc, sortBy, 'DESC'));
              },
            },
            ...(current === undefined
              ? []
              : [
                  {
                    label: t('editor.sortRemove'),
                    onClick: () => {
                      setDoc(setSort(doc, sortBy, undefined));
                    },
                  },
                ]),
          ]
        : [];
      if (shelf === 'values') {
        return [
          ...sorting,
          ...AGGREGATIONS.map((aggregation) => ({
            label: `${i18n.t('patterns:reporting.shelf.aggregation')}: ${t(`editor.aggregations.${aggregation}`)}`,
            disabled: item.key.startsWith(`${aggregation.toLowerCase()}:`),
            onClick: () => {
              setDoc(setAggregation(doc, item.key, aggregation));
            },
          })),
          ...presentation,
        ];
      }
      if ((shelf === 'rows' || shelf === 'columns') && doc.reportType !== 'TABLE') {
        const type = types.get(item.key);
        if (type === 'DATE' || type === 'DATETIME') {
          return [
            ...sorting,
            ...[undefined, ...BUCKETS].map((bucket) => ({
              label: `${i18n.t('patterns:reporting.shelf.bucket')}: ${t(`editor.buckets.${bucket ?? 'NONE'}`)}`,
              onClick: () => {
                setDoc(setBucket(doc, shelf, item.key, bucket));
              },
            })),
            ...presentation,
          ];
        }
      }
      return [...sorting, ...presentation];
    };

  const formatTargetEntry =
    formatTarget === undefined
      ? undefined
      : shelfItemsOf(doc, formatTarget.shelf, t, locale).find((i) => i.key === formatTarget.key);
  const formatKey =
    formatTarget === undefined
      ? ''
      : formatTarget.shelf === 'values'
        ? formatTarget.key
        : (shelfEntries(doc, formatTarget.shelf).find((e) => e.key === formatTarget.key)?.field ??
          '');
  const currentTitle = (() => {
    if (formatTarget === undefined) return undefined;
    switch (formatTarget.shelf) {
      case 'values':
        return doc.pivot?.values.find((v) => valueKey(v) === formatTarget.key)?.label;
      case 'rows':
        return doc.pivot?.rows?.find((r) => r.field === formatTarget.key)?.label;
      case 'columns':
        return doc.reportType === 'TABLE'
          ? doc.table?.columns.find((c) => c.field === formatTarget.key)?.label
          : doc.pivot?.columns?.find((c) => c.field === formatTarget.key)?.label;
      case 'filters':
        return doc.filters?.clauses[Number(formatTarget.key)]?.label;
    }
  })();

  const header = (
    <PageHeader
      title={doc.name}
      breadcrumbs={[
        { label: t('viewer.breadcrumb'), to: links.reporting.reports() },
        { label: doc.name, to: links.reporting.report(code) },
        { label: t('editor.breadcrumb') },
      ]}
      description={[
        t('editor.dataset', {
          name:
            doc.dataset.kind === 'CATALOG'
              ? doc.dataset.name
              : `${custom.data?.dataset?.name ?? doc.dataset.code} (v${String(doc.dataset.version)})`,
        }),
      ].join(' · ')}
      actions={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip
            size="small"
            label={`${t('editor.draft', { version: draftVersion })} · ${save.isPending || dirty ? t('editor.saving') : savedAt === undefined ? '' : t('editor.saved', { at: formatInstant(savedAt, locale) })}`.replace(
              / · $/,
              '',
            )}
          />
          <TextField
            select
            size="small"
            label={t('visibility.label')}
            value={head.visibility}
            disabled={!editable}
            onChange={(event) => {
              const visibility = event.target.value as 'PRIVATE' | 'WORKSPACE';
              update.mutate(
                { code, etag: head.etag, version: head.version, visibility },
                {
                  onSuccess: (next) => {
                    setHead((prev) => ({
                      ...prev,
                      visibility,
                      ...(next.etag === undefined ? {} : { etag: next.etag }),
                      ...(next.version === undefined ? {} : { version: next.version }),
                    }));
                  },
                  onError: (error) => {
                    if (isConflictError(error)) setConflict(true);
                  },
                },
              );
            }}
            sx={{ minWidth: 170 }}
          >
            <MenuItem value="PRIVATE">{t('visibility.PRIVATE')}</MenuItem>
            <MenuItem value="WORKSPACE" disabled={!permissions.can(p.reports.share)}>
              {t('visibility.WORKSPACE')}
            </MenuItem>
          </TextField>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setJsonText(JSON.stringify(doc, null, 2));
              setJsonError(undefined);
              setJsonOpen(true);
            }}
          >
            {t('editor.json')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => void navigate({ href: links.reporting.report(code) })}
          >
            {t('editor.view')}
          </Button>
          {head.publishedVersion > 0 && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setRestoreError(false);
                setVersionsOpen(true);
              }}
            >
              {t('editor.versions')}
            </Button>
          )}
          {editable && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setSwitchTo(undefined);
                setSwitching(true);
              }}
            >
              {t('editor.switchDataset')}
            </Button>
          )}
          {(hasDraft || dirty) && head.publishedVersion > 0 && (
            <Button
              size="small"
              variant="outlined"
              disabled={!editable || discard.isPending}
              onClick={() => {
                setDiscarding(true);
              }}
            >
              {t('editor.discard')}
            </Button>
          )}
          <RequirePermission anyOf={[p.reports.delete]}>
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={!editable}
              onClick={() => {
                setDeleting(true);
              }}
            >
              {t('editor.delete')}
            </Button>
          </RequirePermission>
          <Button
            size="small"
            variant="contained"
            disabled={!editable || publish.isPending || conflict}
            onClick={() => {
              void (async () => {
                let current: { etag?: string; version?: string } = head;
                if (dirty) {
                  const moved = await persist(doc);
                  if (moved === undefined) return;
                  current = moved;
                }
                setPublishedAs(undefined);
                publish.mutate(
                  { code, etag: current.etag, version: current.version },
                  {
                    onSuccess: (next) => {
                      setHasDraft(false);
                      setNotice(undefined);
                      setPublishedAs(next.publishedVersion ?? draftVersion);
                      setHead((prev) => ({
                        ...prev,
                        publishedVersion: next.publishedVersion ?? prev.publishedVersion,
                        ...(next.etag === undefined ? {} : { etag: next.etag }),
                        ...(next.version === undefined ? {} : { version: next.version }),
                      }));
                    },
                    onError: (error) => {
                      if (isConflictError(error)) setConflict(true);
                    },
                  },
                );
              })();
            }}
          >
            {t('editor.publish', { version: draftVersion })}
          </Button>
        </Stack>
      }
    />
  );

  return (
    <PageLayout header={header} maxWidth="none">
      <LiveRegion message={announcement} />
      <Stack spacing={2}>
        {conflict && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  onReload();
                }}
              >
                {t('editor.reload')}
              </Button>
            }
          >
            <AlertTitle>{t('editor.conflictTitle')}</AlertTitle>
            {t('editor.conflictBody')}
          </Alert>
        )}
        {publishedAs !== undefined && (
          <Alert
            severity="success"
            onClose={() => {
              setPublishedAs(undefined);
            }}
            data-testid="published-as"
          >
            {t('editor.publishedAs', { version: publishedAs })}
          </Alert>
        )}
        {notice !== undefined && (
          <Alert
            severity="info"
            onClose={() => {
              setNotice(undefined);
            }}
          >
            {notice}
          </Alert>
        )}
        {publishRefusal !== undefined && !isConflictError(publish.error) && (
          <Alert severity="warning">
            <AlertTitle>{codeMessage(t, publishRefusal).title}</AlertTitle>
            {codeMessage(t, publishRefusal).hint}
          </Alert>
        )}
        {refusal !== undefined && (
          <Alert severity="error">
            <AlertTitle>{codeMessage(t, refusal.code).title}</AlertTitle>
            {refusal.violations.map((v) => (
              <Typography key={`${v.field}:${v.description}`} variant="body2" component="div">
                <code>{v.field}</code> — {v.description}
              </Typography>
            ))}
          </Alert>
        )}
        {newer > pinned && (
          <Alert
            severity="info"
            action={
              <Button
                size="small"
                color="inherit"
                onClick={() => {
                  if (doc.dataset.kind === 'CUSTOM')
                    setDoc({ ...doc, dataset: { ...doc.dataset, version: newer } });
                }}
              >
                {t('editor.repin', { version: newer })}
              </Button>
            }
          >
            {t('editor.newerVersion', { version: newer, pinned })}
          </Alert>
        )}
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', lg: '260px 340px minmax(0, 1fr)' },
            alignItems: 'start',
          }}
        >
          <Paper variant="outlined" sx={(theme) => ({ p: 1.5, ...cardSurfaceSx(theme) })}>
            <FieldList
              fields={fieldListOf(datasetFields, doc)}
              onMove={moveField}
              {...(editable
                ? {
                    onAddCalculated: () => {
                      setCalcEditing(undefined);
                      setExpressionOpen(true);
                    },
                    fieldActions: (field: { name: string; type: string }) => {
                      const calc = (doc.calculatedFields ?? []).find((c) => c.name === field.name);
                      if (calc === undefined) return [];
                      const used = usesField(doc, calc.name);
                      return [
                        {
                          label: t('editor.editCalculated'),
                          onClick: () => {
                            setCalcEditing(calc);
                            setExpressionOpen(true);
                          },
                        },
                        {
                          label: used
                            ? t('editor.removeCalculatedInUse')
                            : t('editor.removeCalculated'),
                          disabled: used,
                          onClick: () => {
                            setDoc({
                              ...doc,
                              calculatedFields: (doc.calculatedFields ?? []).filter(
                                (c) => c.name !== calc.name,
                              ),
                            });
                          },
                        },
                      ];
                    },
                  }
                : {})}
            />
          </Paper>
          <Paper variant="outlined" sx={(theme) => ({ p: 1.5, ...cardSurfaceSx(theme) })}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle2" component="h2">
                {t('editor.structure')}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={doc.reportType}
                aria-label={t('editor.reportType')}
                onChange={(_, value: ReportType | null) => {
                  if (value !== null) setDoc(setReportType(doc, value));
                }}
              >
                {(['TABLE', 'PIVOT', 'CHART'] as const).map((type) => (
                  <ToggleButton key={type} value={type}>
                    {t(`types.${type}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {shelvesOf(doc.reportType).map((shelf) => (
                <FieldShelf
                  key={shelf}
                  shelf={shelf}
                  label={shelfTitle(doc, shelf, t)}
                  items={shelfItemsOf(doc, shelf, t, locale)}
                  max={shelfLimit(doc, shelf)}
                  targets={shelvesOf(doc.reportType)}
                  onDrop={(drop) => {
                    onDrop(shelf, drop);
                  }}
                  onMoveTo={(key, to) => {
                    onDrop(to, { kind: 'token', from: shelf, key });
                  }}
                  onReorder={(from, to) => {
                    const items = shelfItemsOf(doc, shelf, t, locale);
                    setDoc(reorder(doc, shelf, from, to));
                    setAnnouncement(
                      t('editor.reordered', {
                        item: items[from]?.label ?? '',
                        position: to + 1,
                        count: items.length,
                      }),
                    );
                  }}
                  onRemove={(key) => {
                    const item = shelfItemsOf(doc, shelf, t, locale).find((i) => i.key === key);
                    setDoc(removeToken(doc, shelf, key));
                    setAnnouncement(
                      t('editor.removed', {
                        item: item?.label ?? key,
                        shelf: shelfTitle(doc, shelf, t),
                      }),
                    );
                  }}
                  displayActions={displayActions(shelf)}
                />
              ))}
              {doc.reportType === 'CHART' && (
                <Stack spacing={1}>
                  <Typography variant="subtitle2" component="h3">
                    {t('editor.chartType')}
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={doc.chart?.type ?? 'BAR'}
                    aria-label={t('editor.chartType')}
                    onChange={(_, value: ChartDoc['type'] | null) => {
                      if (value !== null)
                        setDoc({ ...doc, chart: { ...(doc.chart ?? {}), type: value } });
                    }}
                    sx={{ flexWrap: 'wrap' }}
                  >
                    {CHART_TYPES.map((type) => (
                      <ToggleButton key={type} value={type}>
                        {t(`editor.chartTypes.${type}`)}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  <TextField
                    select
                    size="small"
                    label={t('editor.chartOptions.legend')}
                    value={doc.chart?.legend ?? 'RIGHT'}
                    onChange={(event) => {
                      setDoc({
                        ...doc,
                        chart: {
                          ...(doc.chart ?? { type: 'BAR' }),
                          legend: event.target.value as 'RIGHT' | 'BOTTOM' | 'NONE',
                        },
                      });
                    }}
                  >
                    <MenuItem value="RIGHT">{t('editor.chartOptions.legendRIGHT')}</MenuItem>
                    <MenuItem value="BOTTOM">{t('editor.chartOptions.legendBOTTOM')}</MenuItem>
                    <MenuItem value="NONE">{t('editor.chartOptions.legendNONE')}</MenuItem>
                  </TextField>
                  {(
                    [
                      'showValues',
                      'yFromZero',
                      ...(doc.chart?.type === 'PIE' ? (['donut'] as const) : []),
                    ] as const
                  ).map((option) => (
                    <FormControlLabel
                      key={option}
                      control={
                        <Switch
                          size="small"
                          checked={doc.chart?.[option] ?? option === 'yFromZero'}
                          onChange={(event) => {
                            setDoc({
                              ...doc,
                              chart: {
                                ...(doc.chart ?? { type: 'BAR' }),
                                [option]: event.target.checked,
                              },
                            });
                          }}
                        />
                      }
                      label={t(`editor.chartOptions.${option}`)}
                    />
                  ))}
                </Stack>
              )}
              <Typography variant="caption" color="text.secondary">
                {t('editor.sortSummary', {
                  sort:
                    ((doc.reportType === 'TABLE' ? doc.table?.sort : doc.pivot?.sort) ?? [])
                      .map((s) => `${displayFieldName(s.by)} ${s.direction === 'ASC' ? '↑' : '↓'}`)
                      .join(', ') || t('editor.sortNone'),
                  cells: new Intl.NumberFormat(locale).format(doc.pivot?.maxCells ?? 10000),
                })}
              </Typography>
            </Stack>
          </Paper>
          <Paper
            variant="outlined"
            sx={(theme) => ({ p: 1.5, minWidth: 0, ...cardSurfaceSx(theme) })}
          >
            <Stack spacing={1.5}>
              <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" component="h2">
                  {t('editor.preview')}
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={
                    mode === 'auto'
                      ? doc.reportType === 'TABLE'
                        ? 'table'
                        : doc.reportType === 'CHART'
                          ? 'chart'
                          : 'pivot'
                      : mode
                  }
                  aria-label={t('editor.preview')}
                  onChange={(_, value: ResultMode | null) => {
                    if (value !== null) setMode(value);
                  }}
                >
                  <ToggleButton value="table">{t('editor.previewModes.table')}</ToggleButton>
                  <ToggleButton value="pivot" disabled={doc.reportType === 'TABLE'}>
                    {t('editor.previewModes.pivot')}
                  </ToggleButton>
                  <ToggleButton value="chart" disabled={doc.reportType === 'TABLE'}>
                    {t('editor.previewModes.chart')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              {!isRunnable(doc) ? (
                <Typography variant="body2" color="text.secondary">
                  {t('editor.previewEmpty')}
                </Typography>
              ) : (
                <>
                  <Box data-testid="preview-run-state">
                    <QueryRunState
                      phase={polling.phase === 'IDLE' ? 'QUEUED' : polling.phase}
                      elapsedSeconds={polling.elapsedSeconds}
                      {...(previewId === undefined ? {} : { queryId: previewId })}
                      empty={query?.state === 'SUCCEEDED' && !hasResultData(query)}
                      {...(failure === undefined
                        ? {}
                        : {
                            errorCode: failure,
                            errorTitle: message.title,
                            errorHint: message.hint,
                          })}
                      {...(query?.error?.message === undefined
                        ? {}
                        : { engineMessage: query.error.message })}
                      warning={failure === 'TOO_MANY_GROUPS'}
                      {...(query?.finishedAt === undefined
                        ? {}
                        : { resultAt: formatInstant(query.finishedAt, locale) })}
                      onRerun={() => {
                        preview.mutate(
                          { code, document: doc as unknown as Record<string, unknown> },
                          {
                            onSuccess: (q) => {
                              setPreviewId(q.id);
                            },
                          },
                        );
                      }}
                    />
                  </Box>
                  <ReportResult doc={doc} query={query} settings={workspace} mode={mode} />
                </>
              )}
            </Stack>
          </Paper>
        </Box>
      </Stack>

      <Dialog
        open={picker !== undefined}
        onClose={() => {
          setPicker(undefined);
        }}
        maxWidth="xs"
        fullWidth
        // Named on the dialog itself, not by its title: the title unmounts while the dialog fades out.
        slotProps={{ paper: { 'aria-label': t('editor.addFilter') } }}
      >
        {picker !== undefined && (
          <>
            <DialogTitle id="operator-picker-title">
              {t('editor.operatorTitle', { field: displayFieldName(picker.field) })}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                  select
                  size="small"
                  label={t('editor.operator')}
                  value={picker.op}
                  onChange={(event) => {
                    const op = event.target.value;
                    // Switching operators keeps what fits: a list keeps its values, one value
                    // keeps the first, a range starts from the first.
                    const values =
                      LIST_OPS.includes(op) || op === RANGE_OP
                        ? picker.values
                        : picker.values.slice(0, 1);
                    setPicker({ ...picker, op, values });
                    setMissing(undefined);
                  }}
                >
                  {operatorsFor(types.get(picker.field))
                    // A range is two clauses joined by AND: under "beliebig" (ANY) it would not be one.
                    .filter((op) => op !== RANGE_OP || doc.filters?.join !== 'ANY')
                    .map((op) => (
                      <MenuItem key={op} value={op}>
                        {t(`editor.operators.${op}` as never)}
                      </MenuItem>
                    ))}
                </TextField>
                <FilterValueInput
                  key={`${picker.op}:${String(picker.index ?? 'new')}`}
                  type={types.get(picker.field) ?? 'STRING'}
                  op={picker.op}
                  values={picker.values}
                  onChange={(values) => {
                    setPicker({ ...picker, values });
                    setMissing(undefined);
                  }}
                  {...(datasetRef?.kind === 'CATALOG' && types.get(picker.field) === 'STRING'
                    ? { loadValues: loadPickerValues }
                    : {})}
                  timeZone={zone}
                  onTimeZoneChange={setZone}
                  {...(missing === undefined ? {} : { error: t(`editor.${missing}`) })}
                />
                <TextField
                  size="small"
                  label={t('editor.filterLabel')}
                  value={picker.label}
                  onChange={(event) => {
                    setPicker({ ...picker, label: event.target.value });
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={picker.editable}
                      onChange={(event) => {
                        setPicker({ ...picker, editable: event.target.checked });
                      }}
                    />
                  }
                  label={t('editor.editable')}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button
                size="small"
                onClick={() => {
                  setPicker(undefined);
                }}
              >
                {t('editor.cancel')}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  const result = clausesOfPicker(picker, types.get(picker.field));
                  if ('missing' in result) {
                    setMissing(result.missing);
                    return;
                  }
                  const next = putClauses(doc, result.clauses, picker.index, picker.span ?? 1);
                  setDoc(next);
                  setAnnouncement(
                    t('editor.moved', {
                      item: displayFieldName(picker.field),
                      shelf: shelfTitle(doc, 'filters', t),
                      position: next.filters?.clauses.length ?? 1,
                      count: next.filters?.clauses.length ?? 1,
                    }),
                  );
                  setPicker(undefined);
                }}
              >
                {picker.index === undefined ? t('editor.addFilter') : t('editor.saveFilter')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <FormatDialog
        open={formatTarget !== undefined}
        fieldLabel={formatTargetEntry?.label ?? ''}
        fieldType={
          formatTarget === undefined
            ? 'STRING'
            : formatShelfType(formatTarget.shelf, formatTarget.key)
        }
        {...(doc.formats?.[formatKey] === undefined ? {} : { format: doc.formats[formatKey] })}
        {...(currentTitle === undefined ? {} : { title: currentTitle })}
        defaults={workspace}
        currencyFields={[...types.entries()]
          .filter(([name, type]) => type === 'STRING' && /währung|currency/i.test(name))
          .map(([name]) => name)}
        onApply={(format: ReportFormat | undefined, title) => {
          if (formatTarget !== undefined)
            setDoc(setPresentation(doc, formatTarget.shelf, formatTarget.key, title, format));
          setFormatTarget(undefined);
        }}
        onClose={() => {
          setFormatTarget(undefined);
        }}
      />

      <Suspense fallback={null}>
        <ExpressionDialog
          open={expressionOpen}
          {...(calcEditing === undefined ? {} : { initial: calcEditing })}
          fields={[...types.keys()].filter((name) => name !== calcEditing?.name)}
          fieldTypes={Object.fromEntries(types)}
          codeTitle={(c) => codeMessage(t, c).title}
          onValidate={async (typed, type) => {
            const expression = canonicalExpression(typed, [...types.keys()]);
            const result = await validate.mutateAsync({
              dataset: doc.dataset,
              expression,
              type,
              // The field being edited is checked as its new expression, not against its old one.
              calculatedFields: (doc.calculatedFields ?? []).filter(
                (c) => c.name !== calcEditing?.name,
              ) as unknown as Record<string, unknown>[],
            });
            return {
              valid: result.valid === true,
              ...(result.code === undefined || result.code === '' ? {} : { code: result.code }),
              ...(result.message === undefined || result.message === ''
                ? {}
                : { message: result.message }),
              ...(result.resultType === undefined ? {} : { resultType: result.resultType }),
              ...(result.warning === undefined || result.warning === ''
                ? {}
                : { warning: result.warning }),
            };
          }}
          onSave={(typed: CalculatedField) => {
            // The document stores the dataset's spelling of every field the expression names.
            const field = {
              ...typed,
              expression: canonicalExpression(typed.expression, [...types.keys()]),
            };
            // Editing keeps the field's place; a new name follows into every shelf that uses it.
            const original = calcEditing?.name;
            const base = original === undefined ? doc : renameField(doc, original, field.name);
            const calcs = base.calculatedFields ?? [];
            setDoc({
              ...base,
              calculatedFields:
                original === undefined
                  ? [...calcs.filter((c) => c.name !== field.name), field]
                  : calcs
                      .filter((c) => c.name === original || c.name !== field.name)
                      .map((c) => (c.name === original ? field : c)),
            });
            setCalcEditing(undefined);
            setExpressionOpen(false);
          }}
          onClose={() => {
            setCalcEditing(undefined);
            setExpressionOpen(false);
          }}
        />
      </Suspense>

      <Dialog
        open={jsonOpen}
        onClose={() => {
          setJsonOpen(false);
        }}
        maxWidth="md"
        fullWidth
        aria-labelledby="json-title"
      >
        <DialogTitle id="json-title">{t('editor.jsonTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText variant="body2">{t('editor.jsonCopyHint')}</DialogContentText>
          <TextField
            multiline
            fullWidth
            minRows={14}
            maxRows={24}
            size="small"
            sx={{ mt: 1 }}
            label={t('editor.jsonReplace')}
            value={jsonText}
            disabled={!editable}
            slotProps={{ htmlInput: { spellCheck: false, style: { fontFamily: 'monospace' } } }}
            onChange={(event) => {
              setJsonText(event.target.value);
              setJsonError(undefined);
            }}
            {...(jsonError === undefined ? {} : { error: true, helperText: jsonError })}
          />
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={() => {
              setJsonOpen(false);
            }}
          >
            {t('editor.jsonClose')}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!editable}
            onClick={() => {
              let parsed: unknown;
              try {
                parsed = JSON.parse(jsonText);
              } catch {
                setJsonError(t('importDialog.notJson'));
                return;
              }
              const next = asDocument(parsed as Record<string, unknown>);
              if (next === undefined) {
                setJsonError(t('codes.DOCUMENT_INVALID.hint'));
                return;
              }
              void persist({ ...next, code }).then((moved) => {
                if (moved !== undefined) {
                  setDoc({ ...next, code });
                  setJsonOpen(false);
                }
              });
            }}
          >
            {t('editor.jsonApply')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmAction
        open={discarding}
        title={t('editor.discardTitle')}
        description={t('editor.discardBody', { version: head.publishedVersion })}
        severity="destructive"
        confirmLabel={t('editor.discard')}
        busy={discard.isPending}
        onCancel={() => {
          setDiscarding(false);
        }}
        onConfirm={() => {
          discard.mutate(
            { code, etag: head.etag, version: head.version },
            {
              onSuccess: () => {
                setDiscarding(false);
                onReload();
              },
              onError: (error) => {
                setDiscarding(false);
                if (isConflictError(error)) setConflict(true);
              },
            },
          );
        }}
      />

      <Dialog
        open={versionsOpen}
        onClose={() => {
          setVersionsOpen(false);
        }}
        maxWidth="xs"
        fullWidth
        aria-labelledby="versions-title"
      >
        <DialogTitle id="versions-title">
          {t('editor.versionsTitle', { name: doc.name })}
        </DialogTitle>
        <DialogContent>
          {restoreError && (
            <Alert severity="error" sx={{ mb: 1 }}>
              {t('editor.versionLoadError')}
            </Alert>
          )}
          {versions.isLoading ? (
            <Skeleton variant="rounded" height={96} />
          ) : (versions.data ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('editor.versionsEmpty')}
            </Typography>
          ) : (
            <List dense disablePadding>
              {[...(versions.data ?? [])]
                .sort((a, b) => (b.number ?? 0) - (a.number ?? 0))
                .map((v) => (
                  <ListItem
                    key={v.number}
                    disableGutters
                    secondaryAction={
                      editable ? (
                        <Button
                          size="small"
                          onClick={() => {
                            setRestoring(v.number);
                          }}
                        >
                          {t('editor.versionLoad')}
                        </Button>
                      ) : undefined
                    }
                  >
                    <ListItemText
                      primary={`v${String(v.number ?? 0)}${v.number === head.publishedVersion ? ` · ${t('editor.versionCurrent')}` : ''}`}
                      secondary={
                        v.publishedAt === undefined
                          ? undefined
                          : t('editor.versionLine', { at: formatInstant(v.publishedAt, locale) })
                      }
                    />
                  </ListItem>
                ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={() => {
              setVersionsOpen(false);
            }}
          >
            {t('editor.close')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmAction
        open={restoring !== undefined}
        title={t('editor.versionLoadTitle', { number: restoring ?? 0 })}
        description={t('editor.versionLoadBody', { number: restoring ?? 0, next: draftVersion })}
        confirmLabel={t('editor.versionLoad')}
        onCancel={() => {
          setRestoring(undefined);
        }}
        onConfirm={() => {
          const number = restoring;
          if (number === undefined) return;
          setRestoring(undefined);
          // The chosen version becomes the draft (autosave writes it); publishing makes it the next
          // version — history is never rewritten (R-5; owner feedback 2026-11-01).
          void fetchVersion(code, number)
            .then((version) => {
              const restored =
                version.document === undefined ? undefined : asDocument(version.document);
              if (restored === undefined) {
                setRestoreError(true);
                return;
              }
              setDoc({ ...restored, code });
              setVersionsOpen(false);
              setNotice(t('editor.versionLoaded', { number, next: draftVersion }));
            })
            .catch(() => {
              setRestoreError(true);
            });
        }}
      />

      <ConfirmAction
        open={switching}
        title={t('editor.switchTitle')}
        description={t('editor.switchWarning')}
        severity="destructive"
        confirmLabel={t('editor.switchConfirm')}
        onCancel={() => {
          setSwitching(false);
        }}
        onConfirm={() => {
          const target = switchTo;
          if (target === undefined || datasetKey(target.ref) === datasetKey(doc.dataset)) {
            setSwitching(false);
            return;
          }
          void fieldsOf(target.ref).then((fields) => {
            const fresh = newReport(doc.name, target.ref, doc.reportType, fields[0]);
            setDoc({
              ...fresh,
              code,
              visibility: doc.visibility,
              ...(doc.description === undefined ? {} : { description: doc.description }),
            });
            setSwitching(false);
          });
        }}
      >
        <DatasetPicker
          value={switchTo?.key ?? datasetKey(doc.dataset)}
          onChange={(option) => {
            setSwitchTo(option);
          }}
        />
      </ConfirmAction>

      <ConfirmAction
        open={deleting}
        title={t('editor.deleteTitle', { name: doc.name })}
        description={t('editor.deleteBody')}
        severity="destructive"
        confirmLabel={t('editor.deleteConfirm')}
        busy={remove.isPending}
        onCancel={() => {
          setDeleting(false);
        }}
        onConfirm={() => {
          remove.mutate(
            { code, etag: head.etag, version: head.version },
            {
              onSuccess: () => {
                setDeleting(false);
                void navigate({ href: links.reporting.reports() });
              },
            },
          );
        }}
      />
    </PageLayout>
  );
}
